import { useEffect, useState, type FormEvent } from 'react';

import { useAuth } from '../auth/AuthContext';
import { config } from '../config';
import {
  TotpRequired,
  confirmSignUp,
  exchangeSsoCode,
  resendConfirmationCode,
  signIn,
  signUp,
  ssoAuthorizeUrl,
  ssoCallbackParams,
} from '../auth/cognito';

type Mode = 'signin' | 'signup' | 'confirm';

export function SignIn() {
  const { refresh } = useAuth();
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [totp, setTotp] = useState<TotpRequired | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  // Hosted UI redirects back here with `?code=...` or
  // `?error_description=...` — this component itself doubles as /sso/
  // callback's handler rather than a separate route, since App.tsx
  // already renders <SignIn/> for any URL while unauthenticated (there
  // is no router-level "logged out" concept to hook a dedicated route
  // into).
  const [callbackPending, setCallbackPending] = useState(() => Boolean(ssoCallbackParams()));

  useEffect(() => {
    const params = ssoCallbackParams();
    if (!params) return;
    const finish = () => {
      setCallbackPending(false);
      // Strips ?code=/?error= so a page refresh doesn't try to redeem it
      // (or re-show the same rejection) again.
      window.history.replaceState({}, '', '/');
    };
    if ('error' in params) {
      setError(params.error);
      finish();
      return;
    }
    exchangeSsoCode(params.code)
      .then(() => refresh())
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Sign-in with Google failed.');
      })
      .finally(finish);
  }, [refresh]);

  function switchMode(next: Mode) {
    setMode(next);
    setError('');
    setNotice('');
    setTotp(null);
    setCode('');
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setNotice('');
    setBusy(true);
    try {
      if (totp) {
        await totp.submit(code);
        await refresh();
      } else if (mode === 'signin') {
        await signIn(email.trim(), password);
        await refresh();
      } else if (mode === 'signup') {
        const { confirmed } = await signUp(email.trim(), password);
        if (confirmed) {
          switchMode('signin');
          setNotice('Account created. You can sign in now.');
        } else {
          switchMode('confirm');
          setNotice(`We sent a verification code to ${email.trim()}.`);
        }
      } else {
        await confirmSignUp(email.trim(), code.trim());
        switchMode('signin');
        setNotice('Email verified. You can sign in now.');
      }
    } catch (err) {
      if (err instanceof TotpRequired) {
        setTotp(err);
        setNotice('Enter the six-digit code from your authenticator app.');
      } else {
        setError(err instanceof Error ? err.message : 'Something went wrong.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setError('');
    try {
      await resendConfirmationCode(email.trim());
      setNotice('A new code is on its way.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resend the code.');
    }
  }

  const heading = totp
    ? 'Two-factor code'
    : mode === 'signin'
      ? 'Sign in'
      : mode === 'signup'
        ? 'Create an account'
        : 'Verify your email';

  if (callbackPending) {
    return (
      <main className="auth-page">
        <div className="auth-card">
          <div className="auth-brand">
            <h1>ETL Integrator</h1>
          </div>
          <p className="muted">Signing you in…</p>
        </div>
      </main>
    );
  }

  return (
    <main className="auth-page">
      <div className="auth-card">
        <div className="auth-brand">
          <h1>ETL Integrator</h1>
        </div>

        <h2>{heading}</h2>

        {error && (
          <p className="alert alert--error" role="alert">
            {error}
          </p>
        )}
        {notice && (
          <p className="alert alert--info" role="status">
            {notice}
          </p>
        )}

        {/* A convenience for a user who already has a verified
            fibtec-etlgen account — not a second signup path, so this
            never shows outside "sign in" (signup/confirm keep the same
            company-email-required copy they always had). */}
        {!totp && mode === 'signin' && config.googleSsoEnabled && (
          <div className="auth-social">
            <a className="btn btn--google" href={ssoAuthorizeUrl('Google')}>
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                <path
                  fill="#4285F4"
                  d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
                />
                <path
                  fill="#34A853"
                  d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
                />
                <path
                  fill="#FBBC05"
                  d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
                />
                <path
                  fill="#EA4335"
                  d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
                />
              </svg>
              Continue with Google
            </a>
            {/* Google sign-in is accepted for any Google account
                (domain_vetting/handler.py), so this is shown whenever the
                button is. */}
            <p className="field-hint">
              Open to any Google account, including personal ones. A personal
              address gets an organisation of its own.
            </p>
            <p className="auth-divider">or</p>
          </div>
        )}

        <form onSubmit={submit} className="stack">
          {!totp && mode !== 'confirm' && (
            <label className="field">
              <span className="field-label">Company email</span>
              <input
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
              />
            </label>
          )}

          {!totp && mode !== 'confirm' && (
            <label className="field">
              <span className="field-label">Password</span>
              <input
                type="password"
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                required
                minLength={12}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {mode === 'signup' && (
                <span className="field-hint">
                  At least 12 characters, with upper case, lower case and a digit.
                </span>
              )}
            </label>
          )}

          {(mode === 'confirm' || totp) && (
            <label className="field">
              <span className="field-label">
                {totp ? 'Authenticator code' : 'Verification code'}
              </span>
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </label>
          )}

          <button className="btn btn--primary" type="submit" disabled={busy}>
            {busy
              ? 'Working…'
              : totp
                ? 'Verify code'
                : mode === 'signin'
                  ? 'Sign in'
                  : mode === 'signup'
                    ? 'Create account'
                    : 'Verify email'}
          </button>
        </form>

        {!totp && (
          <div className="auth-switch">
            {mode === 'signin' && (
              <button className="btn btn--link" onClick={() => switchMode('signup')}>
                Create an account
              </button>
            )}
            {mode === 'signup' && (
              <>
                <p className="muted">
                  Accounts require a company email address — free and consumer email providers
                  are not accepted.
                </p>
                <button className="btn btn--link" onClick={() => switchMode('signin')}>
                  I already have an account
                </button>
              </>
            )}
            {mode === 'confirm' && (
              <>
                <button className="btn btn--link" onClick={resend}>
                  Resend code
                </button>
                <button className="btn btn--link" onClick={() => switchMode('signin')}>
                  Back to sign in
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
